import { useEffect, useState } from 'react';

export interface TocItem {
  id: string;
  label: string;
}

interface TableOfContentsProps {
  items: readonly TocItem[];
  /** Optional title — defaults to "On this page". */
  title?: string;
}

/**
 * Editorial right-rail table of contents. Sticky to the viewport, scroll-spy
 * via IntersectionObserver. Lifted from `_bmad-output/planning-artifacts/
 * ux-design-directions.html` (`.toc`, `.toc-title`, `.toc a` selectors).
 *
 * Only renders at the wide breakpoint and above (≥1280px). At narrower widths
 * the page content remains centered without the rail; the TOC stays hidden so
 * we never compress the main column to make room for it.
 *
 * Section anchors must exist in the page DOM as `id={item.id}` on whatever
 * wrapper element marks the start of that section. The component does not
 * inject the IDs — the page is responsible for tagging its own sections.
 */
export function TableOfContents({ items, title = 'On this page' }: TableOfContentsProps) {
  // Initialize to the first item so the user sees a highlighted entry the
  // moment the page renders, before the IntersectionObserver has fired.
  const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;

    const elements = items
      .map((item) => document.getElementById(item.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    // Track the topmost visible section. `rootMargin: 0px 0px -70% 0px` makes
    // a section "active" the moment its top crosses the upper 30% of the
    // viewport — feels right when scrolling, no flicker between adjacent
    // sections.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0]!.target.id);
        }
      },
      {
        rootMargin: '0px 0px -70% 0px',
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: 'smooth' });
    setActiveId(id);
  }

  return (
    <aside
      aria-label={title}
      className="sticky top-24 hidden h-fit w-[200px] shrink-0 pt-2 wide:block"
    >
      <p className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">
        {title}
      </p>
      <nav>
        <ul className="m-0 flex list-none flex-col p-0">
          {items.map((item) => {
            const isActive = activeId === item.id;
            return (
              <li key={item.id} className="m-0 p-0">
                <a
                  href={`#${item.id}`}
                  onClick={(e) => handleClick(e, item.id)}
                  className={[
                    'block border-l py-1.5 pl-3 text-[13px] font-medium transition-colors',
                    'outline-none focus-visible:rounded-r-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400',
                    isActive
                      ? 'border-l-primary text-primary'
                      : 'border-l-white/[0.06] text-muted hover:border-l-white/[0.15] hover:text-secondary',
                  ].join(' ')}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
}
